import { DatePipe } from '@angular/common';
import { Component, DestroyRef, ElementRef, computed, effect, inject, input, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
    CustomerContactInfo,
    CustomerText,
    CustomerTextAttachment,
    CustomerTextInput,
    FeatureFlagTypes,
    GateReason,
    I18N_OPTIONS_TOKEN,
    NotificationType,
    OriginEntityType,
    PhonePipe,
} from '@raptor-fe/core';
import {
    ApplicationContextStore,
    CustomerTextApiService,
    FileApiService,
    NamedRoute,
    RouterService,
    TranslationPipe,
    TranslationService,
} from '@raptor-fe/services';
import { NotificationStore } from '../../notification-panel/notification.store';
import { downloadAttachment } from '../attachment-download';
import { buildAttachmentBlobName } from '../attachment-upload';
import { translateCustomerTextStatus } from '../message-status';
import { CustomerTextRealtimeStore, CustomerTextStatusUpdate } from '../customer-text-realtime.store';
import { gateMessage, gateReason } from '../gating';
import { hasOriginRoute, originRoute } from '../origin-navigation';

/**
 * The conversation surface CONTENT (Spec #76394 Frontends part). Rendered inside the non-modal
 * draggable Kendo window the launcher owns. Self-contained and component-scoped: it holds its thread
 * state locally (never in a feature store, per DECISION D5, so it can be reused across feature libs)
 * and its only external dependency is the shared API/services layer.
 *
 * Ported from `omni-web-mockups/customer-messaging/conversation-widget`. The template and styles are
 * copied verbatim (Tier 1); the computed formulas (`gate`, `canSend`, `overLimit`, `hasContent`,
 * `isMms`, lightbox) are kept identical (Tier 2); the mock JSON data, the demo-harness scenario
 * flags, and the faked attach/send/refresh are replaced by `CustomerTextApiService`,
 * `ApplicationContextStore` feature flags, and the real `FileApiService` upload path (Tier 3).
 */
@Component({
    selector: 'lib-conversation-widget',
    imports: [FormsModule, DatePipe, PhonePipe, TranslationPipe],
    templateUrl: './conversation-widget.component.html',
    styleUrls: ['./conversation-widget.component.scss'],
})
export class ConversationWidgetComponent {
    private readonly api = inject(CustomerTextApiService);
    private readonly fileApi = inject(FileApiService);
    private readonly appContext = inject(ApplicationContextStore);
    private readonly routerService = inject(RouterService);
    private readonly translationService = inject(TranslationService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly notificationStore = inject(NotificationStore);
    private readonly realtimeStore = inject(CustomerTextRealtimeStore);
    private readonly datePipe = new DatePipe(inject(I18N_OPTIONS_TOKEN).locale);

    /** The customer currently loaded, used to distinguish a customer switch from a live message arrival. */
    private loadedCustomerId: string | null = null;

    /** Ids of the customer-message notifications already reflected in the thread, so only a genuinely new one re-pulls. */
    private readonly seenMessageNotificationIds = new Set<string>();

    private readonly messagesContainer = viewChild<ElementRef<HTMLElement>>('history');
    private readonly composerInput = viewChild<ElementRef<HTMLTextAreaElement>>('composerInput');

    readonly customerId = input.required<string>();
    /** Read-only customer identity + consent state, resolved by the launcher and passed straight through. */
    readonly contact = input<CustomerContactInfo | null>(null);
    readonly originEntityId = input<string | null>(null);
    readonly originEntityType = input<OriginEntityType | null>(null);

    protected readonly maxLength = 1600;

    private readonly tenantId = this.appContext.tenantId;
    private readonly branch = this.appContext.branch;

    /** CustomerTexting feature flag -- gates the whole feature per tenant. */
    protected readonly featureEnabled = computed(() => this.appContext.features().includes(FeatureFlagTypes.CustomerTexting));

    protected readonly thread = signal<CustomerText[]>([]);
    protected readonly draft = signal('');
    protected readonly pendingAttachments = signal<CustomerTextAttachment[]>([]);
    protected readonly refreshing = signal(false);
    protected readonly sending = signal(false);
    /** Populated when a send/upload is refused; surfaced in the composer without clearing the draft. */
    protected readonly sendError = signal<string | null>(null);

    /** Distinct, non-conflated gating reason -- mirrors the backend authoritative gate (shared helper). */
    protected readonly gate = computed<GateReason>(() => gateReason(this.contact() ?? undefined, this.featureEnabled()));

    protected readonly canSend = computed(() => this.gate() === 'ok');

    /** Localized gate message resolved through the shared `gateMessage` source so every messaging surface agrees. */
    protected readonly gateText = computed(() => gateMessage(this.gate(), this.translationService));

    /**
     * Outbound media allowed only when the CustomerTextMediaSend sub-flag is on. Per DEV-DECISION D3
     * the backend enables that flag only on direct-provider deployments, so the frontend gates on the
     * single flag (the mockup's separate `directProvider` switch collapses into it in production).
     */
    protected readonly canSendMedia = computed(() => this.appContext.features().includes(FeatureFlagTypes.CustomerTextMediaSend));

    protected readonly remaining = computed(() => this.maxLength - this.draft().length);
    protected readonly overLimit = computed(() => this.draft().length > this.maxLength);
    protected readonly hasContent = computed(() => this.draft().trim().length > 0 || this.pendingAttachments().length > 0);

    constructor() {
        effect(() => {
            const id = this.customerId();
            // Live-push source: the root notification store prepends inbound-message notifications as they
            // arrive over SignalR, so reading it here lets an open conversation refresh itself the moment a
            // reply lands, instead of only on a manual refresh.
            const notifications = this.notificationStore.results();

            untracked(() => {
                const messageNotificationIds = notifications
                    .filter(
                        (n) =>
                            n.type === NotificationType.CustomerMessage &&
                            n.referenceType === 'Customer' &&
                            n.referenceId === id &&
                            !!n.id,
                    )
                    .map((n) => n.id as string);

                if (id !== this.loadedCustomerId) {
                    // The window was pointed at a different customer: reset the composer and live-tracking,
                    // treat every existing notification as already seen, then load that customer's thread.
                    this.loadedCustomerId = id;
                    this.seenMessageNotificationIds.clear();
                    messageNotificationIds.forEach((notificationId) => this.seenMessageNotificationIds.add(notificationId));
                    this.draft.set('');
                    this.pendingAttachments.set([]);
                    this.sendError.set(null);
                    this.loadThread(id);
                    return;
                }

                // Same customer: re-pull only when a not-yet-seen message notification for them has arrived.
                const hasNewMessage = messageNotificationIds.some((notificationId) => !this.seenMessageNotificationIds.has(notificationId));
                messageNotificationIds.forEach((notificationId) => this.seenMessageNotificationIds.add(notificationId));
                if (hasNewMessage) {
                    this.loadThread(id);
                }
            });
        });

        // Keep the newest message in view: scroll the history to the bottom whenever the thread changes
        // (open, a sent message, or a live arrival). Deferred so the DOM has rendered the new rows first.
        effect(() => {
            this.thread();
            setTimeout(() => this.scrollThreadToBottom());
        });

        // Live delivery-status: when a status change is pushed for a message in this thread, advance its
        // status indicator in place, without re-pulling or a manual refresh.
        effect(() => {
            const update = this.realtimeStore.lastStatusUpdate();
            if (update) {
                untracked(() => this.applyStatusUpdate(update));
            }
        });

        // Same-instance send relay: when a message is sent from another open surface (the Message Center)
        // for the customer this window shows, append it so both surfaces stay in sync without a refresh.
        effect(() => {
            const sent = this.realtimeStore.lastSentMessage();
            if (sent) {
                untracked(() => this.appendRelayedMessage(sent));
            }
        });

        // Server-originated message (for example an inspection report sent from the server): re-pull the
        // thread when the pushed conversation is the one this window shows, so the message appears live.
        effect(() => {
            const update = this.realtimeStore.lastConversationUpdate();
            if (update) {
                untracked(() => {
                    if (update.customerId === this.customerId()) {
                        this.loadThread(this.customerId());
                    }
                });
            }
        });
    }

    /** Appends a message relayed from another surface when it belongs to this thread and isn't already shown. */
    private appendRelayedMessage(message: CustomerText): void {
        if (message.customerId !== this.customerId()) {
            return;
        }
        if (this.thread().some((m) => m.id === message.id)) {
            return;
        }
        this.thread.update((msgs) => [...msgs, message]);
    }

    /** Patches the matching message's status (and any failure reason) from a live delivery-status push. */
    private applyStatusUpdate(update: CustomerTextStatusUpdate): void {
        this.thread.update((messages) =>
            messages.map((message) =>
                message.id === update.messageId
                    ? {
                          ...message,
                          status: update.statusId ? { id: update.statusId, name: update.statusName ?? update.statusId } : message.status,
                          failureReason: update.failureReason ?? message.failureReason,
                      }
                    : message,
            ),
        );
    }

    /** Scrolls the message history to the bottom so the most recent message is visible. */
    private scrollThreadToBottom(): void {
        const container = this.messagesContainer()?.nativeElement;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }

    /**
     * Returns focus to the composer after a send so the user can keep typing without reaching for the mouse.
     * Deferred so it runs after the send's `sending` state has re-enabled the textarea (a disabled element
     * cannot take focus).
     */
    private focusComposer(): void {
        setTimeout(() => this.composerInput()?.nativeElement.focus());
    }

    protected isMms(m: CustomerText): boolean {
        return (m.attachments?.length ?? 0) > 0;
    }

    /** True when the message's origin resolves a wired route and should render the origin-link icon. */
    protected showOriginLink(m: CustomerText): boolean {
        return !!m.originEntityId && hasOriginRoute(m.originEntityType);
    }

    /** Localized origin-link label/tooltip. Literal keys per type keep the build extractor happy. */
    protected originLabelFor(m: CustomerText): string {
        switch (m.originEntityType) {
            case 'Inspection':
                return this.translationService.getTranslation('Customer Messaging', 'Conversation Widget', 'View inspection');
            case 'WorkOrder':
                return this.translationService.getTranslation('Customer Messaging', 'Conversation Widget', 'View work order');
            case 'Quote':
                return this.translationService.getTranslation('Customer Messaging', 'Conversation Widget', 'View quote');
            default:
                return '';
        }
    }

    /** Navigate to the message's origin detail (page behind; the floating window stays open). */
    protected goToOrigin(m: CustomerText): void {
        if (!m.originEntityId) {
            return;
        }
        const route: NamedRoute<object> | null = originRoute(m.originEntityType, {
            tenantId: this.tenantId(),
            branchId: this.branch().id ?? '',
            id: m.originEntityId,
        });
        if (route) {
            this.routerService.navigateTo(route);
        }
    }

    /** Localized "Read by {user} at {time}" tooltip, or null while the message is unread. */
    protected readByTooltip(m: CustomerText): string | null {
        if (!m.readDate) {
            return null;
        }
        const readBy = this.translationService.getTranslation('Customer Messaging', 'Conversation Widget', 'Read by');
        const at = this.translationService.getTranslation('Customer Messaging', 'Conversation Widget', 'at');
        return `${readBy} ${m.readByUserName} ${at} ${this.datePipe.transform(m.readDate, 'short')}`;
    }

    /** Downloads a stored attachment to the viewer's device. */
    protected download(attachment: CustomerTextAttachment): void {
        void downloadAttachment(attachment.url, attachment.name);
    }

    /** Localized label for a message's delivery status (the API value is an English identifier). */
    protected statusLabel(name: string | null | undefined): string {
        return translateCustomerTextStatus(name, this.translationService);
    }

    /** Manual refresh -- re-pulls the full thread and re-marks read on view, alongside the live push-driven reload. */
    protected refresh(): void {
        this.refreshing.set(true);
        this.api
            .getThread(this.tenantId(), this.customerId())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (thread) => {
                    this.thread.set(thread);
                    this.markUnreadInboundAsRead(thread);
                    this.refreshing.set(false);
                },
                error: () => this.refreshing.set(false),
            });
    }

    /**
     * Opens the file picker's selection: uploads the chosen image through the existing file path and
     * adds a pending attachment referencing the returned blob name. Raw bytes are never sent to the
     * message API -- the send carries only the `internalName` reference.
     */
    protected onAttachSelected(event: Event): void {
        if (!this.canSendMedia()) {
            return;
        }
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file) {
            return;
        }
        const blobName = buildAttachmentBlobName(file);
        this.fileApi
            .upload(this.tenantId(), this.branch().id ?? '', file, blobName)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (uploaded) => {
                    this.pendingAttachments.update((list) => [
                        ...list,
                        {
                            id: blobName,
                            internalName: uploaded.internalName ?? blobName,
                            url: URL.createObjectURL(file),
                            name: uploaded.name ?? file.name,
                            contentType: uploaded.mimeType ?? file.type,
                            status: 'Stored',
                        },
                    ]);
                },
                error: () =>
                    this.sendError.set(
                        this.translationService.getTranslation(
                            'Customer Messaging',
                            'Conversation Widget',
                            'Attachment upload failed. Please try again.',
                        ),
                    ),
            });
    }

    protected removeAttachment(id: string): void {
        this.pendingAttachments.update((list) => {
            const found = list.find((a) => a.id === id);
            if (found?.url) {
                URL.revokeObjectURL(found.url);
            }
            return list.filter((a) => a.id !== id);
        });
    }

    /**
     * Sends the composed message. The backend re-verifies the gate authoritatively, persists, and
     * dispatches, returning the persisted row which is appended to the thread. A refusal surfaces in
     * the composer and preserves the draft and pending attachments so nothing typed is lost.
     */
    /** Sends on Enter (Shift+Enter still inserts a newline, since Angular's keydown.enter ignores modified keys). */
    protected onComposerEnter(event: Event): void {
        event.preventDefault();
        this.send();
    }

    protected send(): void {
        if (this.gate() !== 'ok' || this.overLimit() || !this.hasContent() || this.sending()) {
            return;
        }
        const input: CustomerTextInput = {
            body: this.draft().trim() || null,
            attachments: this.pendingAttachments(),
            originEntityId: this.originEntityId(),
            originEntityType: this.originEntityType(),
        };
        this.sending.set(true);
        this.sendError.set(null);
        this.api
            .send(this.tenantId(), this.customerId(), input)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (message) => {
                    this.thread.update((msgs) => [...msgs, message]);
                    this.realtimeStore.publishSentMessage(message);
                    this.pendingAttachments().forEach((a) => {
                        if (a.url) {
                            URL.revokeObjectURL(a.url);
                        }
                    });
                    this.draft.set('');
                    this.pendingAttachments.set([]);
                    this.sending.set(false);
                    this.focusComposer();
                },
                error: (err) => {
                    this.sendError.set(
                        err?.error?.message ??
                            this.translationService.getTranslation(
                                'Customer Messaging',
                                'Conversation Widget',
                                'Message could not be sent. Please try again.',
                            ),
                    );
                    this.sending.set(false);
                    this.focusComposer();
                },
            });
    }

    /** Loads the customer's full thread and marks unread inbound messages read on view (DECISION D4). */
    private loadThread(customerId: string): void {
        this.api
            .getThread(this.tenantId(), customerId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((thread) => {
                this.thread.set(thread);
                this.markUnreadInboundAsRead(thread);
            });
    }

    /**
     * DECISION D4: as the conversation opens, mark every unread inbound message read and patch the
     * returned row (with its read-by audit) back into the thread so the indicator updates in place.
     * Unread derives from `readDate == null`, never from the status name (DECISION 6).
     */
    private markUnreadInboundAsRead(thread: CustomerText[]): void {
        const unread = thread.filter((m) => m.direction === 'Inbound' && m.readDate === null);
        for (const message of unread) {
            this.api
                .markRead(this.tenantId(), message.id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((updated) => this.thread.update((msgs) => msgs.map((m) => (m.id === updated.id ? updated : m))));
        }
        if (unread.length > 0) {
            this.dismissMessageNotifications();
        }
    }

    /**
     * Dismisses the panel notification(s) that alerted about this customer's messages once those messages
     * are read. A customer-message notification references the customer, not an individual message, so
     * reading the conversation clears its alerts.
     */
    private dismissMessageNotifications(): void {
        const customerId = this.customerId();
        this.notificationStore
            .results()
            .filter((n) => n.type === NotificationType.CustomerMessage && n.referenceType === 'Customer' && n.referenceId === customerId && !!n.id)
            .forEach((n) => this.notificationStore.markAsDismissed({ notificationId: n.id as string }));
    }
}
